import { OrganizationPublicId, UUIDString } from "../service/server/Types";

import { NetworkRequestState } from "../models";
import { RefreshFn } from "./api";
import { useDataRequest } from "./api";
import { Api } from "../service/Api";
import { Error as RequestErrors } from "../../polaris/copy/ropa";
import { RoPADetailsDto, RoPASummaryDto } from "../service/server/dto/RoPADto";
import { useCallback } from "react";
import axios from "axios";
import { ApiRouteHelpers } from "../root/apiRoutes";
import { Fmt } from "../../polaris/formatters";

export const useRoPAFunctions = (organizationId: OrganizationPublicId) => {
  const publishRoPA = useCallback(async () => {
    return await Api.ropa.saveLatestRoPA(organizationId);
  }, [organizationId]);

  const deleteRoPA = useCallback(
    async (ropaId: string) => {
      return await Api.ropa.deleteRoPA(organizationId, ropaId);
    },
    [organizationId],
  );

  const setMeasures = useCallback(
    async (measuresMarkup: string) => {
      return await Api.ropa.setMeasures(organizationId, measuresMarkup);
    },
    [organizationId],
  );

  return {
    publishRoPA,
    deleteRoPA,
    setMeasures,
  };
};

export const useRoPASummary = (
  organizationId: OrganizationPublicId,
): [RoPASummaryDto, NetworkRequestState, RefreshFn] => {
  const {
    result: ropaSummary,
    request,
    refresh,
  } = useDataRequest({
    queryKey: ["ropa", organizationId, "summary"],
    staleTime: 5 * 1000, // 5 seconds
    cacheTime: 5 * 1000,
    api: () => Api.ropa.getRoPASummary(organizationId),
    messages: {
      defaultError: RequestErrors.Summary,
    },
    dependencies: [organizationId],
  });

  return [ropaSummary, request, refresh];
};

export const useRoPADetails = (
  organizationId: OrganizationPublicId,
): [RoPADetailsDto, NetworkRequestState, RefreshFn] => {
  const {
    result: ropaDetails,
    request,
    refresh,
  } = useDataRequest({
    queryKey: ["ropa", organizationId, "details"],
    staleTime: 5 * 1000, // 5 seconds
    cacheTime: 5 * 1000,
    api: () => Api.ropa.getRoPADetails(organizationId),
    messages: {
      defaultError: RequestErrors.Summary,
    },
    dependencies: [organizationId],
  });

  return [ropaDetails, request, refresh];
};

/*
 * A list of all previous RoPA snapshots.
 */
export const useRoPAHistory = (
  organizationId: OrganizationPublicId,
): [RoPASummaryDto[], NetworkRequestState, RefreshFn] => {
  const {
    result: ropaHistory,
    request,
    refresh,
  } = useDataRequest({
    queryKey: ["ropa", organizationId, "history"],
    staleTime: 5 * 1000, // 5 seconds
    cacheTime: 5 * 1000,
    api: () => Api.ropa.getRoPAHistory(organizationId),
    messages: {
      defaultError: RequestErrors.Summary,
    },
    dependencies: [organizationId],
  });

  return [ropaHistory, request, refresh];
};

export const useRoPADownloader = (orgId: OrganizationPublicId) => {
  const downloadRoPA = useCallback(
    async (ropaId: UUIDString, filename: string = null) => {
      try {
        const response = await axios.get(
          ApiRouteHelpers.organization.documents.ropa.download(orgId, ropaId),
          {
            responseType: "blob", // important for binary file download
          },
        );

        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        // Create a link element for download
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          filename ? `${filename}.pdf` : `RoPA ${Fmt.Date.fmtDate(new Date().toString())}.pdf`,
        );

        // Append it to the body to make it work in Firefox
        document.body.appendChild(link);
        link.click();

        // Clean up and remove the link element
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error downloading PDF:", error);
      }
    },
    [orgId],
  );

  /**
   * Open a RoPA as a PDF without downloading it - e.g., for preview.
   */
  const openRoPA = useCallback(
    async (ropaId: UUIDString, filename: string = null) => {
      try {
        const response = await axios.get(
          ApiRouteHelpers.organization.documents.ropa.download(orgId, ropaId),
          {
            responseType: "blob",
          },
        );

        const blob = new Blob([response.data], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);

        const customFileName = filename
          ? `${filename}.pdf`
          : `RoPA ${Fmt.Date.fmtDate(new Date().toString())}.pdf`;
        const url = blobUrl + "#" + encodeURIComponent(customFileName);

        window.open(url, "_blank");

        // Cleanup
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      } catch (error) {
        console.error("Error opening PDF:", error);
      }
    },
    [orgId],
  );

  return {
    downloadRoPA,
    openRoPA,
  };
};
